<template>
  <div class="editor-container" ref="editorContainer">
    <v-progress-linear
      v-if="isPageLoading"
      :active="isPageLoading"
      :indeterminate="isPageLoading"
      absolute
      top
      style="top:50px;"
      color="primary"
    ></v-progress-linear>

    <document-editor-header 
        v-model="document" 
        :selected-page-index.sync="pageIndex" 
        :zoom-value="zoomValue"  
        :is-modal="isModal"
        :was-modified="wasModified"
        @zoom="onToggleZoom"
        @close="$emit('close')"
        ref="header"
    ></document-editor-header>
    
    <v-progress-linear
      v-if="isPageLoading && !currentPage"
      :active="isPageLoading"
      :indeterminate="isPageLoading"
      absolute
      top
      color="primary"
    ></v-progress-linear>
    <div class="page-container">
      <document-page 
          v-if="currentPage" 
          :loading="isPageLoading" 
          :key="pageUniqueId"
          ref="page" 
          v-model="currentPage" 
          :zoom="zoomValue" 
          :header.sync="documentHeader"
          :footer.sync="documentFooter"
          @ready="onEditorsReady"
          @imageUploaded="onImageUploaded"
      ></document-page>
      <document-actions 
          v-model="isShowActions" 
          @action="onAction" 
          :has-page="!!currentPage" 
          :document.sync="document"
          :loading="isPageLoading"
      ></document-actions>
    </div>
    <select-template 
        ref="templateSelector" 
        filter-type="vef" 
        @selected="addNewPageFromTemplate"
    ></select-template>
    <document-settings 
        ref="documentSettings" 
        v-model="document" 
        :is-modal="false" 
        :draggable="true" 
        :loading="isPageLoading"
        :projectId="projectId"
    ></document-settings>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from 'vue';

export const windowSize = () => {
  let w = ref(window.innerWidth),
    h = ref(window.innerHeight)
  const onSizeChange = () => {
    w.value = window.innerWidth, h.value = window.innerHeight;
  }
  onMounted(() => window.addEventListener('resize', onSizeChange))
  onUnmounted(() => window.removeEventListener('resize', onSizeChange))
  const width = computed(() => w.value),
    height = computed(() => h.value)
  return { width, height }
}

export default {
  name: 'DocumentEditor',
  components: {
    SelectTemplate: () => import('./TemplateSelector'),
    DocumentPage: () => import('./DocumentPageComponent'),
    DocumentActions: () => import('./DocumentActions'),
    DocumentSettings: () => import('./DocumentSettings'),
    DocumentEditorHeader: () => import('./DocumentEditorHeader'),
  },
  props: {
    projectId: {
      type: String,
      required: true,
    },
    documentId: {
      type: [Number, String],
      required: true,
    },
    isModal: Boolean,
  },
  data: () => ({
    notifyOptions: {
        title: 'Редактор документа',
    },
    document: null,
    pageIndex: 0,
    zoomType: null,
    zoomValue: 1,
    isShowActionButtons: false,
    isShowActions: true,
    isPageLoading: false,
    storedDocument: null,
  }),
  computed: {
    pages: {
      get() {
        return this.document?.pages || [];
      }
    },
    documentHeader: {
      get() {
        return this.document?.header;
      },
      set(v) {
        this.document && (this.document.header = v);
      },
    },
    documentFooter: {
      get() {
        return this.document?.footer;
      },
      set(v) {
        this.document && (this.document.footer = v);
      },
    },
    pageUniqueId() {
      return this.projectId + '/'  + this.documentId + '/' + (this.currentPage?.id || 0);
    },
    currentPage() {
      return !!this.pageIndex && this.pages?.length >= this.pageIndex
              ? this.pages[this.pageIndex - 1]
              : null;
    },
    pageTypes() {
      return this.pages?.length ? this._(this.pages).map(v => v.class).filter(v => !!v).uniq().value() : []; 
    },
    documentUrl() {
      return `/api/${this.projectId}/report/${this.documentId}`;
    },
    pageParams() {
      return this._.pick(this.document, ['padding_left', 'padding_right', 'padding_top', 'padding_bottom', 'primary_color', 'secondary_color']);
    },
    wasModified() {
        return this.storedDocument !== null && !this._.isEqual(this.storedDocument, this.document);
    },
  },
  watch: {
    document: {
      handler(v) {
        this.storedDocument = this._.cloneDeep(v);
        if (v?.pages?.length && v?.pages[0]) {
          this.pageIndex = v.pages[0].num;
        }
      },
      immediate: true,
    },
    'document.immutable_css': {
      handler(v) {
        this.addOrUpdateCss(v, "template-stylesheet-document");
      },
    },
    'document.css': {
      handler(v) {
        this.addOrUpdateCss(v, "template-stylesheet-base");
      },
    },
    pageParams: {
      handler() {
        //this.$refs?.page?.updateContentHeight();
        this.$nextTick(() => this.updateDocumentCss());
      },
      deep: true,
    },
    currentPage() {
      this.resetScrollInOverflowedText(false);
    },
  },
  setup() {
    const size = windowSize();
    return {
      window: size,
    };
  },
  async created() {
    await this.loadDocument();
    this.updateDocumentCss();
  },
  methods: {
    async loadDocument() {
      this.isPageLoading = true;
      this.document = await this.$api.get(this.documentUrl).then(resp => {
        this.notifySuccess(`Документ ${resp?.data?.data.id} успешно загружен`);
        return resp?.data?.data || {};
      }).catch(this.notifyError).finally(() => {
        this.isPageLoading = false;
      })
    },
    hashCode(v) {
      var hash = 0,
        i,
        chr;
      if (v?.length === 0) {
        return hash;
      }
      for (i = 0; i < v?.length; i++) {
        chr = v.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0;
      }
      return hash;
    },
    addNewPageFromTemplate(template) {
      const data = {
        id: crypto.randomUUID(),
        content: template.parts.main,
        is_cover: template.type === 'cover',
        class: template.template.name,
        background: template.parts.background,
        num: (this.pages?.length && this.pages[this.pages.length - 1] && this.pages[this.pages.length - 1].num || 0) + 1,
      };
      if (data.is_cover) {
        data.header = template.parts.header;
        data.footer = template.parts.footer;
      } else {
        if (!this.document?.header) {
          this.$set(this.document, 'header', template.parts.header);
        }
        if (!this.document?.footer) {
          this.$set(this.document, 'footer', template.parts.footer);
        }
      }
      this.pages.push(data);
      this.pageIndex = this.pages?.length;
      this.updateDocumentCss();
    },
    updateDocumentCss() {
      if(!this.document) {
        return;
      }
      this._.each(['padding_left', 'padding_right', 'padding_top', 'padding_bottom', 'primary_color', 'secondary_color'], v => {
        document.documentElement.style.setProperty(`--page-${v}`, this.document[v] + (v.includes('padding') ? 'mm' : ''));
      });
      this.resetScrollInOverflowedText(false);
    },
    async singleHtml() {
      if (!this.currentPage) {
        return '';
      }
      const html =
        `<html>
          <head>
            <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
            <style>${this.document?.immutable_css}</style>
            <style>${this.document?.css}</style>
            <style>
              html {
                --page-padding_left: ${this.document?.padding_left}mm;
                --page-padding_right: ${this.document?.padding_right}mm;
                --page-padding_top: ${this.document?.padding_top}mm;
                --page-padding_bottom: ${this.document?.padding_bottom}mm;
                --page-primary_color: ${this.document?.primary_color}mm;
                --page-secondary_color: ${this.document?.secondary_color}mm;
              }
            </style>
          </head>
          <body>${await this.getAllPages()}</body>
        </html>`;
      return html;
    },
    async getAllPages() {
      return (await (Promise.all(this._.map(this.pages, v => this.$refs?.page?.html(v)))) || []).join("");
    },
    downloadFile(data, filename, type) {
      var file = new Blob([data], {
        type: type,
      });
      if (window.navigator.msSaveOrOpenBlob)
        // IE10+
        window.navigator.msSaveOrOpenBlob(file, filename);
      else {
        // Others
        var a = document.createElement('a'),
          url = URL.createObjectURL(file);
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(async () => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          //await this.loadDocument();
        }, 0);
      }
    },
    updatePageNumbers() {
      let i = 1;
      this._.each(this.pages, v => {
        v.num = i;
        i++;
      });
    },
    async saveDocument(preventUpdateDocument) {
      this.isPageLoading = true;
      this.updatePageNumbers();
      this.updateDocumentCss();
      return this.$refs.documentSettings.save().then(data => {
        this.notifySuccess(`Документ ${data.id} успешно сохранен`);
        if(!preventUpdateDocument) {
          /* for(let i in d) {
            if (i === 'pages') {
              if(d[i].length !== this.document[i].length) {
                this.$set(this.document, i, d[i]);
                continue;
              }
              for (let ip in d[i])  {
                for (let prop in d[i][ip]) {
                  this.document[i][ip][prop] = d[i][ip][prop];
                }
              }
              continue;
            }
            this.document[i] = d[i];
          } */
          this.document = data;
        }
        this.$emit('update', data);
        return data;
      }).finally(() => {
        this.isPageLoading = false;
      });
    },
    async renderPdf() {
      this.$refs.page.resetScroll();
      await new Promise(r => setTimeout(r, 500));
      await this.saveDocument(true);
      this.isPageLoading = true;

      /** @TODO remove it */
      /* const html = await this.singleHtml();
      const win = window.open("", "TEST HTML");
      win.document.body.innerHTML = html; */

      return this.$api.request({
          url: this.$apiUrl.getFullUrl(this.documentUrl + '/download/'),
          method: 'GET',
          responseType: 'blob'
      })
      .then(response => {
        this.notifySuccess(`Pdf документа сформирован успешно`);
        this.downloadFile(
          response.data,
          this.document?.name + '.pdf',
          'application/pdf',
        );
      })
      .catch(this.notifyError).finally(() => {
        this.isPageLoading = false;
      });
    },
    showDocumentSettings() {
      this.$refs.documentSettings.show();
    },
    addPage() {
      this.$refs.templateSelector.show();
    },
    removePage(index = this.pageIndex) {
      this.pages.splice(index - 1, 1);
      this.pageIndex = index > 1 ? index - 1 : 1;
      this.updatePageNumbers();
    },
    setBackground(v) {
      if (!v || !(v instanceof File)) {
        this.notifyError('Некорректные данные при загрузке изображения');
        return;
      }
      this.notifySuccess('Изображение успешно загружено');
      v.convertToBase64(base64 => (this.currentPage.background = base64));
    },
    resetScrollInOverflowedText(useSmooth = true) {
      const scrollParams = {top: 0};
      if (useSmooth) {
        scrollParams.behavior = 'smooth';
      }
      this.$refs?.page?.$el?.parentNode?.scrollTo(scrollParams);
      this.$refs?.editorContainer?.scrollTo(scrollParams);
    },
    addOrUpdateCss (v, id) {
      let style = document.getElementById(id);
      if (!style) {
        style = document.createElement('style');
        style.type = 'text/css';
        style.id = id;
        document.head.appendChild(style);
      }
      try{style.innerHTML = v;}
      //IE fix
      catch(error){style.styleSheet.cssText = v;}
      /* this._.each(this.pages, p => {
        this.addCssFile(p.class);
      }); */
    },
    onAction(data) {
      if (!this[data?.action]) {
        return;
      }
      this[data.action](data.params);
    },
    onToggleZoom() {
      const pageContainerPaddings = 12, // (10 + 10)
            toolbarsHeight = this.$refs.header.$el.offsetHeight;
      const p = this.$refs.page.$el;
      const editor = this.$refs.editorContainer;
      this.zoomType = (this.zoomType < 2 && this.zoomType + 1) || 0;
      let zoom = 1;
      //p.style.transformOrigin = "top center";
      //p.style.transform = "scale(" + zoom + ")";
      //p.style.zoom = zoom;

      if (this.zoomType) {
        this.zoomValue = zoom;
        const pw = p.parentNode.offsetWidth;
        const vh = editor.offsetHeight - toolbarsHeight;
        const w = p.offsetWidth;
        const h = p.offsetHeight;
        if (this.zoomType === 1) {
          zoom = pw / w;
        } else {
          zoom = (vh - pageContainerPaddings) / h;
        }
        //p.style.transform = "scale(" + zoom + ")";
        //p.style.zoom = zoom;
      }
      this.zoomValue = zoom;

      //const pageRect = p.getBoundingClientRect();
      //p.parentNode.style.height = (pageRect.height + pageContainerPaddings - 1) + "px";
      
      this.resetScrollInOverflowedText();
    },
    onEditorsReady() {
      this.resetScrollInOverflowedText();
    },
    onImageUploaded(data) {
      const {file, callback} = data;
      this.$api.postFile(this.documentUrl + '/upload/', file, 'image').then(response => {
        if(!response?.data?.data?.file) {
          this.notifyError('Ошибка загрузки изображения на сервер');
          callback(false);
          return;
        }
        this.notifySuccess(`Изображение загружено на сервер`);
        callback(this.$apiUrl.BACKEND_URL + response.data.data.file);
      }).catch(this.notifyError);
    },
  },
}
</script>
<style lang="scss" scoped>
  .editor-container {
    height: 100%;
    position: relative;
    background: #f9f9f9;
    overflow-y: auto;
  }
  .page-editor {
    display: table-caption;
  }
  .page-container {
    //height: calc(100% - 48px);
    width: 100%;
    padding: 5px 0 7px;
    overflow: hidden;
    //display: flex;
    //flex-direction: column;
  }
  .page-container_flex {
    display: flex;
    height: calc(100% - 48px);
    flex-direction: column;
    justify-content: center;
  }
  .v-navigation-drawer--fixed {
    z-index: 99992;
  }
  .v-navigation-drawer--temporary{
    z-index: 99992;
  }
</style>
